import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { graphql, usePreloadedQuery, useRefetchableFragment } from 'react-relay';

import { fromFlagId, useFlagsService } from '@atlassian/jira-flags';
import { useIssueKey } from '@atlassian/jira-issue-context-service';
import { SuccessFlag } from '@atlassian/jira-issue-create/src/ui/modal/success-flag';
import { HIDE_DONE_SUBTASKS_FILTER } from '@atlassian/jira-issue-shared-types';
import { useUserPreferencesValue } from '@atlassian/jira-issue-user-preference-services';
import { connect } from '@atlassian/jira-issue-view-react-redux';
import NativeIssueTable, { SORT_DESC, type SortOrder } from '@atlassian/jira-native-issue-table';

import JiraRelayEnvironment from '@atlassian/jira-relay-environment';
import type { ui_issueViewCommonViews_ConfigurableChildIssuesPanel$key } from '@atlassian/jira-relay/src/__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanel.graphql';
import ui_main_ConfigurableChildIssuesPanelQuery_IssueTableRefetchableQuery, {
	type ui_main_ConfigurableChildIssuesPanelQuery_IssueTableRefetchableQuery as IssueTableRefetchableQueryType,
} from '@atlassian/jira-relay/src/__generated__/ui_main_ConfigurableChildIssuesPanelQuery_IssueTableRefetchableQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { useChildIssuesSortBy } from '../../../../../hooks';
import { SORT_OPTION_RANK, SORT_OPTION_RANKING } from '../../../../../model/types';
import { getRecentlyCreatedIssue } from '../../../../../state/entities/selectors';
import type { State } from '../../../../../state/types';
import { useColumnLoader } from '../../../utils/column-configurability';
import { useLoadingState } from '../../../utils/loading-state';

export const ConfigurableChildIssuesPanel = ({
	recentlyCreatedIssue,
}: {
	recentlyCreatedIssue: ReturnType<typeof getRecentlyCreatedIssue>;
}) => {
	const cloudId = useCloudId();
	const issueKey = useIssueKey();
	const hideDone = useUserPreferencesValue(HIDE_DONE_SUBTASKS_FILTER);
	const recentlyCreatedIssueKeyRef = useRef('');

	const [sortByValue] = useChildIssuesSortBy();

	const sortField = useMemo(
		() => (!sortByValue || sortByValue === SORT_OPTION_RANKING ? SORT_OPTION_RANK : sortByValue),
		[sortByValue],
	);

	const newJQL = useRef(
		`parent = ${issueKey} ${hideDone[0] === 'true' ? '& status!=done ' : ''} ORDER BY ${sortField} ASC`,
	);

	const attributes = useMemo(
		() => ({
			cloudId,
			issueSearchInput: {
				jql: newJQL.current,
			},
			amountOfColumns: 13,
			namespace: 'ISSUE_NAVIGATOR',
			options: {},
			fieldSetIds: ['issuekey', 'summary'],
			shouldQueryFieldSetsById: true,
			filterId: undefined,
			first: 10,
		}),
		[newJQL, cloudId],
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const data = usePreloadedQuery<any>(
		graphql`
			query ui_main_ConfigurableChildIssuesPanelQuery(
				$cloudId: ID!
				$issueSearchInput: JiraIssueSearchInput!
				$namespace: String
				$viewId: String
				$filterId: String
				$amountOfColumns: Int!
				$fieldSetIds: [String!]!
				$shouldQueryFieldSetsById: Boolean!
				$first: Int!
				$after: String
			) {
				...ui_issueViewCommonViews_ConfigurableChildIssuesPanel
			}
		`,
		{
			kind: 'PreloadedQuery',
			environment: JiraRelayEnvironment,
			fetchKey: 'issue',
			fetchPolicy: 'store-or-network',
			name: 'issueViewCommon',
			variables: attributes,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			dispose: () => {},
			isDisposed: false,
		},
	);

	const [results, refetch] = useRefetchableFragment<
		IssueTableRefetchableQueryType,
		ui_issueViewCommonViews_ConfigurableChildIssuesPanel$key
	>(
		graphql`
			fragment ui_issueViewCommonViews_ConfigurableChildIssuesPanel on Query
			@refetchable(
				queryName: "ui_main_ConfigurableChildIssuesPanelQuery_IssueTableRefetchableQuery"
			) {
				jira @required(action: THROW) {
					issueSearchStable(
						cloudId: $cloudId
						issueSearchInput: $issueSearchInput
						first: $first
						after: $after
					) @required(action: THROW) {
						...ui_nativeIssueTable_NativeIssueTable_issues @arguments(pageSize: $first)
					}
					fieldSetsById(fieldSetIds: $fieldSetIds, cloudId: $cloudId)
						@required(action: THROW)
						@optIn(to: "JiraFieldSetsById") {
						...ui_nativeIssueTable_NativeIssueTable_columns
					}
				}
			}
		`,
		data,
	);

	const issues = results.jira.issueSearchStable;
	const columns = results.jira.fieldSetsById;

	const { isLoading, refetchWithoutSuspending } = useLoadingState(
		attributes,
		refetch,
		ui_main_ConfigurableChildIssuesPanelQuery_IssueTableRefetchableQuery,
	);

	const { showFlag } = useFlagsService();

	useEffect(() => {
		if (!recentlyCreatedIssue?.issueKey) {
			return;
		}

		if (recentlyCreatedIssueKeyRef.current !== recentlyCreatedIssue.issueKey) {
			refetch(attributes, {
				fetchPolicy: 'store-and-network',
				onComplete: () => {
					recentlyCreatedIssueKeyRef.current = recentlyCreatedIssue.issueKey;

					const flagId = fromFlagId(new Date().getTime().toString());
					showFlag({
						id: flagId,
						render: (flagProps) => (
							<SuccessFlag
								issueDiscoveryUrl={`/browse/${recentlyCreatedIssue.issueKey}`}
								issueList={[
									{
										issueId: Number(recentlyCreatedIssue.issueId),
										issueKey: recentlyCreatedIssue.issueKey,
									},
								]}
								flagKey={flagProps.key}
								id={flagId}
							/>
						),
					});
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [recentlyCreatedIssue?.issueKey]);

	const onPageChange = useCallback(
		(cursor: string) => {
			refetchWithoutSuspending(
				{
					after: cursor,
				},
				{
					fetchPolicy: 'store-or-network',
				},
			);
		},
		[refetchWithoutSuspending],
	);

	const [columnNames, setColumnNames] = useState(['issuekey', 'summary']);
	const onSortChange = useCallback(
		(field: string, direction: SortOrder) => {
			newJQL.current = `parent = ${issueKey} ${hideDone[0] === 'true' ? '& status!=done ' : ''} ORDER BY ${field} ${direction}`;
			refetchWithoutSuspending(
				{
					issueSearchInput: {
						jql: newJQL.current,
					},
				},
				{
					fetchPolicy: 'store-or-network',
				},
				{
					fieldSetIds: columnNames,
				},
			);
		},
		[newJQL, refetchWithoutSuspending, hideDone, issueKey, columnNames],
	);

	// , changeType: any, optimisticFieldSets: any
	const onColumnsChange = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(columnIds: any) => {
			setColumnNames(columnIds);
			refetchWithoutSuspending({
				fieldSetIds: columnIds,
			});
		},
		[refetchWithoutSuspending],
	);

	return (
		<NativeIssueTable
			issues={issues}
			sortField={sortField}
			sortDirection={SORT_DESC}
			columns={columns}
			onPageChange={onPageChange}
			onSortChange={onSortChange}
			loading={isLoading}
			onColumnsChange={onColumnsChange}
			useColumnLoader={useColumnLoader}
		/>
	);
};

export const ConnectedConfigurableChildIssuesPanel = connect((state: State) => ({
	recentlyCreatedIssue: getRecentlyCreatedIssue(state),
}))(ConfigurableChildIssuesPanel);
